import { useSetRecoilState } from "recoil";
import styled from "styled-components";
import { Box, Flex, Image } from "rebass/styled-components";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";

import Remix from "components/atoms/form/button/remix";
import { ModalType } from "MVVM/@types";
import { ClipReactions } from "MVVM/Components/ClipReactions";
import { AllstarModalState } from "MVVM/State/modals";
import { IClip } from "MVVM/@types";
import { Button } from "MVVM/Components/Button";
import { RiShareForwardFill } from "react-icons/ri";
import { CopyLink } from "MVVM/Components/Button/Share";
import { More } from "MVVM/Components/Clip/Components/ClipOptions/Components/MoreClipOptionsDropdown";
import { EPaywallType } from "MVVM/Components/Modals/Paywall";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipDownload } from "MVVM/Components/Modals/ClipDownload";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { analytics } from "MVVM/Analytics";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { DELETE_MONTAGE } from "MVVM/GraphQL/montage";
import { deleteClip } from "MVVM/Utilities/Clip";
import { ISetClipInactiveProps } from "MVVM/Components/Clip/@types";

interface IShareSectionProps {
  clip: IClip;
  onRemixClicked: () => void;
  onClipDeleted?: () => void;
}

const { REACT_APP_S3_CLIP_BUCKET } = process.env;

const ShareSection = memo(
  ({ clip, onRemixClicked, onClipDeleted }: IShareSectionProps) => {
    const { allstarUser } = useUser();
    const history = useHistory();
    const setAllstarModalState = useSetRecoilState(AllstarModalState);
    const [deleteMontage] = useMutation(DELETE_MONTAGE);
    const [isDeleted, setIsDeleted] = useState(false);
    const isInMhButtonTest = false;
    const isOwnClip = allstarUser?.user?._id === clip.user?._id;

    const toggleModal = useCallback(
      (modalType: ModalType) => {
        setAllstarModalState({
          data: clip,
          functions: {},
          isOpen: modalType,
        });
      },
      [clip, setAllstarModalState],
    );

    const {
      portalOpen: quickEditPortalOpen,
      setPortalOpen: setQuickEditPortalOpen,
      Portal: QuickEditPortal,
    } = usePortal(
      <QuickEdit
        momentId={clip?.moment?.id}
        closeModal={() => setQuickEditPortalOpen(false)}
      />,
      "root",
    );

    const setReportModalOpen = useCallback(() => {
      setAllstarModalState({
        data: { _id: clip.id, user: { _id: clip.user._id } },
        functions: {},
        isOpen: ModalType.Report,
      });
    }, [clip.id, clip.user._id, setAllstarModalState]);

    const setClipInactive = useCallback(
      async ({ id, isMontage, deleteMobile }: ISetClipInactiveProps) => {
        const response = await deleteClip({
          id,
          isMontage,
          deleteMobile,
          token: allstarUser.token,
          deleteMontage,
        });

        setIsDeleted(true);
        if (onClipDeleted) {
          onClipDeleted();
        }

        return response;
      },
      [allstarUser.token, deleteMontage, onClipDeleted],
    );

    const setEditModalOpen = useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          setAllstarModalState({
            data: clip,
            functions: { setClipInactive },
            isOpen: ModalType.Delete,
          });
        }
      },
      [clip, setAllstarModalState, setClipInactive],
    );

    const {
      portalOpen: clipDownloadPortalOpen,
      setPortalOpen: setClipDownloadPortalOpen,
      Portal: ClipDownloadPortal,
    } = usePortal(<ClipDownload clip={clip} />, "root");

    const handleDownloadClick = useCallback(async () => {
      setAllstarModalState({
        isOpen: ModalType.Download,
        data: {
          ...clip,
          type: EPaywallType.download as EPaywallType,
        },
        functions: {},
      });
    }, [clip, setAllstarModalState]);

    const handleQuickEditClick = useCallback(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete("remix");
      window.history.replaceState({}, document.title, url.toString());
      onRemixClicked();
      setQuickEditPortalOpen(true);
    }, [setQuickEditPortalOpen, onRemixClicked]);

    const handleClipDownloadClick = useCallback(() => {
      setClipDownloadPortalOpen(true);
    }, [setClipDownloadPortalOpen]);

    const handleShareClick = useCallback(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete("share");
      window.history.replaceState({}, document.title, url.toString());
      toggleModal(ModalType.Share);
    }, [toggleModal]);

    const handleMatchHistoryClick = useCallback(() => {
      analytics.track("Button - Click", {
        location: "clip",
        locationIdentifier: "matchHistoryFromClip",
        source: "website",
      });
      const matchHistoryUrl = `/match-history?game=${getMatchHistoryGameEnum(
        clip.game,
      )}&playerSearchIdentifier=${clip.playerGameIdentifier}`;
      history.push(matchHistoryUrl);
    }, [clip, history]);

    useEffect(() => {
      if (!clip.status) return;
      const urlParams = new URLSearchParams(window.location.search);
      const saveQueryParam = urlParams.get("save");
      const shareQueryParam = urlParams.get("share");
      const remixQueryParam = urlParams.get("remix");

      if (saveQueryParam && allstarUser.user) setClipDownloadPortalOpen(true);
      if (shareQueryParam) toggleModal(ModalType.Share);
      if (remixQueryParam && clip.isRemixable) {
        onRemixClicked();
        setQuickEditPortalOpen(true);
      }
    }, [
      clip.status,
      clip.isRemixable,
      setClipDownloadPortalOpen,
      toggleModal,
      setQuickEditPortalOpen,
      allstarUser.user,
      onRemixClicked,
    ]);

    const remixProps = useMemo(
      () => ({
        classification: undefined,
        classificiation: undefined,
        clip,
        disableOnClick: false,
        onClick: handleQuickEditClick,
        setModalOpen: handleQuickEditClick,
      }),
      [clip, handleQuickEditClick],
    );

    const moreProps = useMemo(
      () => ({
        clip,
        isOwnClip,
        setShareModalOpen: handleShareClick,
        setReportModalOpen,
        setEditModalOpen,
        onDownloadClick: handleDownloadClick,
        onMobileDownloadClick: handleClipDownloadClick,
        position: { top: "-20px", right: "28px" },
      }),
      [
        clip,
        isOwnClip,
        handleShareClick,
        setReportModalOpen,
        setEditModalOpen,
        handleDownloadClick,
        handleClipDownloadClick,
      ],
    );

    const shouldShowMatchHistory =
      !clip.moment?.id && !!clip.playerGameIdentifier;

    return (
      <>
        {isDeleted && (
          <DeletedNotification>Clip deleted successfully</DeletedNotification>
        )}
        <ShareContainer>
          {clipDownloadPortalOpen && ClipDownloadPortal}
          {quickEditPortalOpen && QuickEditPortal}
          {clip.isRemixable ? (
            <Remix {...remixProps} />
          ) : shouldShowMatchHistory && isInMhButtonTest ? (
            <StyledMatchHistoryButton
              classification="rounded"
              size="small"
              flex={true}
              alignItems="center"
              justifyContent="center"
              onClick={handleMatchHistoryClick}
            >
              Match History
            </StyledMatchHistoryButton>
          ) : clip.type === "SQUAD_MONTAGE" ? (
            <Image
              width={125}
              my={4}
              src={`${REACT_APP_S3_CLIP_BUCKET}/static/web/montages/SquadMontage_Horizontal@2x.webp`}
            />
          ) : clip.type === "MATCH_MONTAGE" ? (
            <Image
              width={125}
              my={4}
              src={`${REACT_APP_S3_CLIP_BUCKET}/static/web/montages/MatchMontage_Horizontal@2x.webp`}
            />
          ) : (
            <></>
          )}
          <StyledClipReactions clip={clip} mobileRight="-200px" />
          <CopyLink
            clip={clip}
            clipLink={`${window.location.origin}/clip/${clip.shareId}`}
            onClick={undefined}
            isMontage={false}
            isProfile={false}
          ></CopyLink>
          <StyledShareButton
            classification="secondaryRounded"
            onClick={handleShareClick}
          >
            <StyledShareIcon size={20} />
          </StyledShareButton>
          <More {...moreProps} />
        </ShareContainer>
      </>
    );
  },
);

const ShareContainer = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 25px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    padding: 0;
    transform: scale(0.9);
  }
`;

const StyledShareButton = styled(Button)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  display: flex;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

const StyledShareIcon = styled(RiShareForwardFill)``;

const StyledClipReactions = styled(ClipReactions)`
  min-height: 40px;
  width: 80px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    height: 36px;
    width: 65px;
  }
`;

const StyledMatchHistoryButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 500;
  flex-shrink: 0;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

const DeletedNotification = styled(Box)`
  animation: slideIn 0.3s ease-out;
  background-color: ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 500;
  margin: 0 25px 16px 25px;
  padding: 16px 24px;
  text-align: center;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 14px;
    margin: 0 0 12px 0;
    padding: 12px 16px;
  }
`;

export { ShareSection };
